package com.gsb.vyapar.bhagya.datasource.cache

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Boolean
import kotlin.ByteArray
import kotlin.Long
import kotlin.String

public class GsbVyaparBhagyaDatabaseQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> getCategories(mapper: (
    id: Long,
    name: String,
    imageUrl: String,
    deleted: Boolean,
  ) -> T): Query<T> = Query(-280_153_111, arrayOf("categoryEntity"), driver,
      "GsbVyaparBhagyaDatabase.sq", "getCategories", """
  |SELECT categoryEntity.id, categoryEntity.name, categoryEntity.imageUrl, categoryEntity.deleted
  |FROM categoryEntity
  |WHERE deleted = 0
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getBoolean(3)!!
    )
  }

  public fun getCategories(): Query<CategoryEntity> = getCategories { id, name, imageUrl, deleted ->
    CategoryEntity(
      id,
      name,
      imageUrl,
      deleted
    )
  }

  public fun <T : Any> getSubCategories(mapper: (
    id: Long,
    name: String,
    categoryId: Long,
    deleted: Boolean,
  ) -> T): Query<T> = Query(1_017_525_135, arrayOf("subCategoryEntity"), driver,
      "GsbVyaparBhagyaDatabase.sq", "getSubCategories", """
  |SELECT subCategoryEntity.id, subCategoryEntity.name, subCategoryEntity.categoryId, subCategoryEntity.deleted
  |FROM subCategoryEntity
  |WHERE deleted = 0
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getBoolean(3)!!
    )
  }

  public fun getSubCategories(): Query<SubCategoryEntity> = getSubCategories { id, name, categoryId,
      deleted ->
    SubCategoryEntity(
      id,
      name,
      categoryId,
      deleted
    )
  }

  public fun <T : Any> getSubCategoriesOfCategory(categoryId: Long?, mapper: (
    id: Long,
    name: String,
    categoryId: Long,
    deleted: Boolean,
  ) -> T): Query<T> = GetSubCategoriesOfCategoryQuery(categoryId) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getBoolean(3)!!
    )
  }

  public fun getSubCategoriesOfCategory(categoryId: Long?): Query<SubCategoryEntity> =
      getSubCategoriesOfCategory(categoryId) { id, name, categoryId_, deleted ->
    SubCategoryEntity(
      id,
      name,
      categoryId_,
      deleted
    )
  }

  public fun getImage(url: String): Query<ByteArray> = GetImageQuery(url) { cursor ->
    cursor.getBytes(0)!!
  }

  public fun countImagesCache(): Query<Long> = Query(-1_875_061_212, arrayOf("imageCache"), driver,
      "GsbVyaparBhagyaDatabase.sq", "countImagesCache", "SELECT COUNT(*) FROM imageCache") {
      cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> getBusiness(i: Long, mapper: (
    v: Long,
    d: String,
    i: Long,
    u: String,
  ) -> T): Query<T> = GetBusinessQuery(i) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getString(3)!!
    )
  }

  public fun getBusiness(i: Long): Query<BusinessEntity> = getBusiness(i) { v, d, i_, u ->
    BusinessEntity(
      v,
      d,
      i_,
      u
    )
  }

  public fun <T : Any> getAllTheBusinesses(mapper: (
    v: Long,
    d: String,
    i: Long,
    u: String,
  ) -> T): Query<T> = Query(1_938_695_083, arrayOf("businessEntity"), driver,
      "GsbVyaparBhagyaDatabase.sq", "getAllTheBusinesses",
      "SELECT businessEntity.v, businessEntity.d, businessEntity.i, businessEntity.u FROM businessEntity") {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getString(3)!!
    )
  }

  public fun getAllTheBusinesses(): Query<BusinessEntity> = getAllTheBusinesses { v, d, i, u ->
    BusinessEntity(
      v,
      d,
      i,
      u
    )
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertCategory(
    name: String,
    imageUrl: String,
    deleted: Boolean,
  ): Long {
    val result = driver.execute(381_788_128, """
        |INSERT OR REPLACE
        |INTO categoryEntity (
        |    name,
        |    imageUrl,
        |    deleted
        |) VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, name)
          bindString(1, imageUrl)
          bindBoolean(2, deleted)
        }.await()
    notifyQueries(381_788_128) { emit ->
      emit("categoryEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun updateCategory(
    id: Long?,
    name: String,
    imageUrl: String,
    deleted: Boolean,
  ): Long {
    val result = driver.execute(-71_170_576, """
        |INSERT OR REPLACE
        |INTO categoryEntity (
        |    id,
        |    name,
        |    imageUrl,
        |    deleted
        |) VALUES (?, ?, ?, ?)
        """.trimMargin(), 4) {
          bindLong(0, id)
          bindString(1, name)
          bindString(2, imageUrl)
          bindBoolean(3, deleted)
        }.await()
    notifyQueries(-71_170_576) { emit ->
      emit("categoryEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertSubCategory(
    name: String,
    categoryId: Long,
    deleted: Boolean,
  ): Long {
    val result = driver.execute(604_059_964, """
        |INSERT OR REPLACE
        |INTO subCategoryEntity (
        |    name,
        |    categoryId,
        |    deleted
        |) VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, name)
          bindLong(1, categoryId)
          bindBoolean(2, deleted)
        }.await()
    notifyQueries(604_059_964) { emit ->
      emit("subCategoryEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun updateSubCategory(
    id: Long?,
    name: String,
    categoryId: Long,
    deleted: Boolean,
  ): Long {
    val result = driver.execute(1_298_553_132, """
        |INSERT OR REPLACE
        |INTO subCategoryEntity (
        |    id,
        |    name,
        |    categoryId,
        |    deleted
        |) VALUES (?, ?, ?, ?)
        """.trimMargin(), 4) {
          bindLong(0, id)
          bindString(1, name)
          bindLong(2, categoryId)
          bindBoolean(3, deleted)
        }.await()
    notifyQueries(1_298_553_132) { emit ->
      emit("subCategoryEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun updateImageCache(
    url: String,
    image: ByteArray,
    last_accessed: Long,
  ): Long {
    val result = driver.execute(1_853_786_969, """
        |INSERT OR REPLACE
        |INTO imageCache (
        |    url,
        |    image,
        |    last_accessed
        |) VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, url)
          bindBytes(1, image)
          bindLong(2, last_accessed)
        }.await()
    notifyQueries(1_853_786_969) { emit ->
      emit("imageCache")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun updateAccessTime(last_accessed: Long, url: String): Long {
    val result = driver.execute(-2_113_995_069,
        """UPDATE imageCache SET last_accessed = ? WHERE url = ?""", 2) {
          bindLong(0, last_accessed)
          bindString(1, url)
        }.await()
    notifyQueries(-2_113_995_069) { emit ->
      emit("imageCache")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun evictOldest(`value`: Long): Long {
    val result = driver.execute(1_341_384_511, """
        |DELETE FROM imageCache WHERE url IN (
        |    SELECT url FROM imageCache ORDER BY last_accessed ASC LIMIT ?
        |)
        """.trimMargin(), 1) {
          bindLong(0, value)
        }.await()
    notifyQueries(1_341_384_511) { emit ->
      emit("imageCache")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun updateBusiness(
    i: Long?,
    v: Long,
    u: String,
    d: String,
  ): Long {
    val result = driver.execute(-1_268_512_590, """
        |INSERT OR REPLACE
        |INTO businessEntity (
        |    i,
        |    v,
        |    u,
        |    d
        |) VALUES (?, ?, ?, ?)
        """.trimMargin(), 4) {
          bindLong(0, i)
          bindLong(1, v)
          bindString(2, u)
          bindString(3, d)
        }.await()
    notifyQueries(-1_268_512_590) { emit ->
      emit("businessEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun clearCategories(): Long {
    val result = driver.execute(-156_485_536, """DELETE FROM categoryEntity""", 0).await()
    notifyQueries(-156_485_536) { emit ->
      emit("categoryEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun clearSubCategories(): Long {
    val result = driver.execute(116_311_992, """DELETE FROM subCategoryEntity""", 0).await()
    notifyQueries(116_311_992) { emit ->
      emit("subCategoryEntity")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun clearImageCache(): Long {
    val result = driver.execute(1_372_760_363, """DELETE FROM imageCache""", 0).await()
    notifyQueries(1_372_760_363) { emit ->
      emit("imageCache")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun clearBusiness(): Long {
    val result = driver.execute(1_130_984_196, """DELETE FROM businessEntity""", 0).await()
    notifyQueries(1_130_984_196) { emit ->
      emit("businessEntity")
    }
    return result
  }

  private inner class GetSubCategoriesOfCategoryQuery<out T : Any>(
    public val categoryId: Long?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("subCategoryEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("subCategoryEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_565_364_452, """
    |SELECT subCategoryEntity.id, subCategoryEntity.name, subCategoryEntity.categoryId, subCategoryEntity.deleted
    |FROM subCategoryEntity
    |WHERE deleted = 0
    |AND
    |categoryId = IFNULL(?,categoryId)
    """.trimMargin(), mapper, 1) {
      bindLong(0, categoryId)
    }

    override fun toString(): String = "GsbVyaparBhagyaDatabase.sq:getSubCategoriesOfCategory"
  }

  private inner class GetImageQuery<out T : Any>(
    public val url: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("imageCache", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("imageCache", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_310_756_718, """SELECT image FROM imageCache WHERE url = ?""", mapper,
        1) {
      bindString(0, url)
    }

    override fun toString(): String = "GsbVyaparBhagyaDatabase.sq:getImage"
  }

  private inner class GetBusinessQuery<out T : Any>(
    public val i: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("businessEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("businessEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_376_404_275, """
    |SELECT businessEntity.v, businessEntity.d, businessEntity.i, businessEntity.u
    |FROM businessEntity
    |WHERE i = ?
    """.trimMargin(), mapper, 1) {
      bindLong(0, i)
    }

    override fun toString(): String = "GsbVyaparBhagyaDatabase.sq:getBusiness"
  }
}
